import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { MerlinSearchActions } from '@src/suite/common/store/merlin/actions/ai.search.actions';
import { MerlinMediaType } from '@src/suite/common/store/merlin/models/ai.seach.models';
import { selectMediaSearchTerm } from '@src/suite/common/store/merlin/selectors/ai.search.selectors';
import { PlaybookMerlinSearchService } from '@src/suite/playbook/services/playbook-merlin-search-service';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';

@Injectable()
export class MerlinSearchEffects {
    actions$ = inject(Actions);
    playbookMerlinSearchService = inject(PlaybookMerlinSearchService);
    store = inject(Store);

    searchMedia = (searchTerm: string) => {
        return forkJoin([this.playbookMerlinSearchService.searchMedia(searchTerm, MerlinMediaType.PHOTO), this.playbookMerlinSearchService.searchMedia(searchTerm, MerlinMediaType.VIDEO)]).pipe(
            map(([photos, videos]) => MerlinSearchActions.searchPhotoAndVideoSuccess({ photos, videos, searchTerm })),
            catchError(error => of(MerlinSearchActions.searchPhotoAndVideoError({ error })))
        );
    };

    generatePhotosAndVideosSearchTerm$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MerlinSearchActions.generatePhotoAndVideoSearchTerm),
            switchMap(action => {
                if (action.queryText?.trim()) {
                    return this.playbookMerlinSearchService.generateMediaSearchTerm(action.queryText).pipe(
                        switchMap(res => this.searchMedia(res)),
                        catchError(error => of(MerlinSearchActions.searchPhotoAndVideoError({ error })))
                    );
                }
                return of(MerlinSearchActions.clearSearchResults());
            })
        );
    });

    searchPhotosAndVideos$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MerlinSearchActions.photoAndVideoSearchTermGenerated),
            concatLatestFrom(() => this.store.select(selectMediaSearchTerm)),
            switchMap(([action, searchTerm]) => {
                if (action.searchTerm !== searchTerm) {
                    return this.searchMedia(action.searchTerm);
                }
                return of(MerlinSearchActions.emptyAction());
            })
        );
    });

    searchEntities$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MerlinSearchActions.searchEntity),
            switchMap(action => {
                if (action.queryText?.trim()) {
                    return this.playbookMerlinSearchService.searchEntity(action.queryText).pipe(map(searchResults => MerlinSearchActions.searchEntitySuccess({ searchResults })));
                }
                return of(MerlinSearchActions.clearSearchResults());
            })
        );
    });
}
